/**
 * Safe Quantum Proposal Manager
 *
 * UI component for managing quantum encryption/decryption proposals
 * that require Safe multi-signature approval
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  Key as KeyIcon,
  CheckCircle as ApprovedIcon,
  Schedule as PendingIcon,
  Cancel as RejectedIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Timer as TimerIcon,
  Assignment as AssignmentIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import type { SafeInfo } from '@safe-global/safe-apps-sdk';
import type { QuantumVaultProposal, SafeQuantumIntegration } from '@/services/SafeQuantumIntegration';

interface SafeQuantumProposalManagerProps {
  safeInfo: SafeInfo;
  quantumIntegration: SafeQuantumIntegration;
  currentOwner: string;
  onProposalUpdate?: (proposal: QuantumVaultProposal) => void;
}

interface ProposalAction {
  type: 'approve' | 'reject' | 'execute' | 'view';
  enabled: boolean;
  label: string;
  color: 'primary' | 'success' | 'error' | 'secondary';
}

export const SafeQuantumProposalManager: React.FC<SafeQuantumProposalManagerProps> = ({
  safeInfo,
  quantumIntegration,
  currentOwner,
  onProposalUpdate
}) => {
  const [proposals, setProposals] = useState<QuantumVaultProposal[]>([]);
  const [selectedProposal, setSelectedProposal] = useState<QuantumVaultProposal | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState<string | null>(null);

  // Load proposals on component mount
  useEffect(() => {
    loadProposals();
  }, []);

  const loadProposals = async () => {
    try {
      setIsLoading(true);
      const pendingProposals = quantumIntegration.getPendingProposals();
      setProposals(pendingProposals);
    } catch (error) {
      setError(`Failed to load proposals: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveProposal = async (proposalId: string) => {
    try {
      setIsProcessing(proposalId);
      setError(null);

      const consent = await quantumIntegration.approveQuantumProposal(proposalId);

      // Update proposal in local state
      const updatedProposal = quantumIntegration.getProposal(proposalId);
      if (updatedProposal) {
        setProposals(prev => prev.map(p => p.id === proposalId ? updatedProposal : p));
        onProposalUpdate?.(updatedProposal);
      }

      console.log(`Proposal ${proposalId} approved by ${consent.owner}`);
    } catch (error) {
      setError(`Failed to approve proposal: ${error}`);
    } finally {
      setIsProcessing(null);
    }
  };

  const handleExecuteProposal = async (proposalId: string) => {
    try {
      setIsProcessing(proposalId);
      setError(null);

      const result = await quantumIntegration.executeQuantumEncryption(proposalId);

      if (result.success) {
        // Update proposal status
        const updatedProposal = quantumIntegration.getProposal(proposalId);
        if (updatedProposal) {
          setProposals(prev => prev.map(p => p.id === proposalId ? updatedProposal : p));
          onProposalUpdate?.(updatedProposal);
        }
      } else {
        setError(result.error || 'Execution failed');
      }
    } catch (error) {
      setError(`Failed to execute proposal: ${error}`);
    } finally {
      setIsProcessing(null);
    }
  };

  const getProposalActions = (proposal: QuantumVaultProposal): ProposalAction[] => {
    const actions: ProposalAction[] = [];
    const isOwner = safeInfo.owners.includes(currentOwner);

    if (proposal.status === 'pending') {
      actions.push({
        type: 'approve',
        enabled: isOwner && !isProcessing,
        label: 'Approve',
        color: 'primary'
      });
    }

    if (proposal.status === 'approved') {
      actions.push({
        type: 'execute',
        enabled: isOwner && !isProcessing,
        label: 'Execute',
        color: 'success'
      });
    }

    actions.push({
      type: 'view',
      enabled: true,
      label: 'Details',
      color: 'secondary'
    });

    return actions;
  };

  const getStatusIcon = (status: QuantumVaultProposal['status']) => {
    switch (status) {
      case 'pending': return <PendingIcon color="warning" />;
      case 'approved': return <ApprovedIcon color="success" />;
      case 'executed': return <ApprovedIcon sx={{ color: 'green' }} />;
      case 'rejected': return <RejectedIcon color="error" />;
      default: return <InfoIcon />;
    }
  };

  const getOperationIcon = (type: QuantumVaultProposal['type']) => {
    switch (type) {
      case 'encrypt': return <LockIcon />;
      case 'decrypt': return <UnlockIcon />;
      case 'key_rotation': return <KeyIcon />;
      case 'vault_setup': return <SecurityIcon />;
      default: return <AssignmentIcon />;
    }
  };

  const formatTimeRemaining = (expirationTime: number): string => {
    const remaining = expirationTime - Date.now();
    if (remaining <= 0) return 'Expired';

    const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
    const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));

    if (days > 0) return `${days}d ${hours}h remaining`;
    return `${hours}h remaining`;
  };

  const getOwnerSignatureStatus = (proposal: QuantumVaultProposal) => {
    // This would ideally track which specific owners have signed
    // For now, we'll show signature progress
    const progress = (proposal.currentSignatures / proposal.requiredSignatures) * 100;
    return { progress, signed: proposal.currentSignatures, required: proposal.requiredSignatures };
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Safe Quantum Proposals
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label={`${safeInfo.threshold} of ${safeInfo.owners.length} signatures required`}
                size="small"
                variant="outlined"
                icon={<SecurityIcon />}
              />
              <Button variant="outlined" size="small" onClick={loadProposals}>
                Refresh
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Proposals List */}
      <Card>
        <CardContent>
          {isLoading ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading quantum proposals...
              </Typography>
            </Box>
          ) : proposals.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <SecurityIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No Quantum Proposals
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create a quantum encryption proposal to get started
              </Typography>
            </Box>
          ) : (
            <List>
              {proposals.map((proposal, index) => {
                const actions = getProposalActions(proposal);
                const signatureStatus = getOwnerSignatureStatus(proposal);

                return (
                  <Box key={proposal.id}>
                    {index > 0 && <Divider />}
                    <ListItem sx={{ py: 2, alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                        {getOperationIcon(proposal.type)}
                        <Box sx={{ ml: 1 }}>
                          {getStatusIcon(proposal.status)}
                        </Box>
                      </Box>

                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {proposal.description}
                            </Typography>
                            <Chip
                              label={proposal.type.toUpperCase()}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={proposal.status.toUpperCase()}
                              size="small"
                              color={
                                proposal.status === 'approved' ? 'success' :
                                proposal.status === 'executed' ? 'primary' :
                                proposal.status === 'rejected' ? 'error' : 'warning'
                              }
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Proposed by: {proposal.proposer}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Assets: {proposal.assets.length} • Security Level: {proposal.quantumMetadata.securityLevel}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <TimerIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                              {formatTimeRemaining(proposal.expirationTime)}
                            </Typography>

                            {/* Signature Progress */}
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Signatures:
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={signatureStatus.progress}
                                sx={{ flex: 1, height: 6, borderRadius: 3 }}
                                color={signatureStatus.progress === 100 ? 'success' : 'primary'}
                              />
                              <Typography variant="caption">
                                {signatureStatus.signed}/{signatureStatus.required}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />

                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                          {actions.map(action => (
                            <Button
                              key={action.type}
                              variant={action.type === 'view' ? 'outlined' : 'contained'}
                              color={action.color}
                              size="small"
                              disabled={!action.enabled || isProcessing === proposal.id}
                              onClick={() => {
                                if (action.type === 'approve') {
                                  handleApproveProposal(proposal.id);
                                } else if (action.type === 'execute') {
                                  handleExecuteProposal(proposal.id);
                                } else if (action.type === 'view') {
                                  setSelectedProposal(proposal);
                                }
                              }}
                              sx={{ minWidth: 80 }}
                            >
                              {isProcessing === proposal.id ? 'Processing...' : action.label}
                            </Button>
                          ))}
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </Box>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Proposal Details Dialog */}
      <Dialog
        open={selectedProposal !== null}
        onClose={() => setSelectedProposal(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedProposal && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getOperationIcon(selectedProposal.type)}
                Quantum Proposal Details
                <Chip label={selectedProposal.status} size="small" />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                {/* Basic Info */}
                <Box>
                  <Typography variant="h6" gutterBottom>Operation Details</Typography>
                  <Typography><strong>Type:</strong> {selectedProposal.type.toUpperCase()}</Typography>
                  <Typography><strong>Description:</strong> {selectedProposal.description}</Typography>
                  <Typography><strong>Proposer:</strong> {selectedProposal.proposer}</Typography>
                  <Typography><strong>Created:</strong> {new Date(selectedProposal.timestamp).toLocaleString()}</Typography>
                  <Typography><strong>Expires:</strong> {new Date(selectedProposal.expirationTime).toLocaleString()}</Typography>
                </Box>

                {/* Quantum Metadata */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Quantum Configuration</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography><strong>Security Level:</strong> Category {selectedProposal.quantumMetadata.securityLevel}</Typography>
                    <Typography><strong>Algorithms:</strong></Typography>
                    <Box sx={{ ml: 2 }}>
                      {selectedProposal.quantumMetadata.algorithms.map(alg => (
                        <Chip key={alg} label={alg} size="small" sx={{ mr: 1, mb: 1 }} />
                      ))}
                    </Box>
                    <Typography><strong>Key Path:</strong> {selectedProposal.quantumMetadata.keyDerivationPath}</Typography>
                    <Typography><strong>Vault ID:</strong> {selectedProposal.quantumMetadata.encryptedVaultId}</Typography>
                  </AccordionDetails>
                </Accordion>

                {/* Assets */}
                {selectedProposal.assets.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6">Assets ({selectedProposal.assets.length})</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {selectedProposal.assets.map((asset, index) => (
                          <ListItem key={index}>
                            <ListItemText
                              primary={`${asset.name || asset.symbol} (${asset.type})`}
                              secondary={`Balance: ${asset.balance} • Address: ${asset.tokenAddress || 'Native'}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Signature Status */}
                <Box>
                  <Typography variant="h6" gutterBottom>Multi-Signature Status</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(selectedProposal.currentSignatures / selectedProposal.requiredSignatures) * 100}
                      sx={{ flex: 1, height: 8, borderRadius: 4 }}
                    />
                    <Typography>
                      {selectedProposal.currentSignatures}/{selectedProposal.requiredSignatures}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {selectedProposal.requiredSignatures - selectedProposal.currentSignatures} more signature(s) needed
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedProposal(null)}>Close</Button>
              {selectedProposal.status === 'pending' && safeInfo.owners.includes(currentOwner) && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    handleApproveProposal(selectedProposal.id);
                    setSelectedProposal(null);
                  }}
                  disabled={isProcessing === selectedProposal.id}
                >
                  Approve Proposal
                </Button>
              )}
              {selectedProposal.status === 'approved' && safeInfo.owners.includes(currentOwner) && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => {
                    handleExecuteProposal(selectedProposal.id);
                    setSelectedProposal(null);
                  }}
                  disabled={isProcessing === selectedProposal.id}
                >
                  Execute Operation
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default SafeQuantumProposalManager;